import { getAggregateError } from "@bracket/catch";
import { awaitValue, cache, } from "@bracket/chainography/helpers";
import type { EpochMilliseconds, } from "@bracket/types";
import { keccak256, toHex,} from "viem";
import type { Config } from "wagmi";
import { multicall, } from "wagmi/actions";
import type { contract, } from "./contract";



export async function multiCaller<T extends object = []>( config: Config, calls: Array<ReturnType<typeof contract>>, ): Promise<[T, AggregateError|null]> {

	const fetchlog: string[] = [];
	let error: AggregateError|null = null;
	const results: unknown[] = [];
	const callTree: Record<number, { call: ReturnType<typeof contract>; ready: boolean; value?: unknown; awaiting: boolean; ttl?: EpochMilliseconds; key?: string; }> = {};

	let idx = 0;

	for(const call of calls) {

		const { ttl, baked, } = call;

		if(typeof baked !== "undefined") {
			callTree[idx] = { call, ready: true, awaiting: false, value: baked, };
			idx++;
			continue;
 		}

		if(!ttl) {
			callTree[idx] = { call, ready: false, awaiting: false, };
			idx++;
			continue;
		}

		const key = keccak256(toHex(JSON.stringify(call)));

		const cached = cache.get(key);
		if(cached) {
			
			const { since, value, ready, fetching, } = cached;
			const expired = since + ttl < Date.now();

			if(ready) {

				if(!expired) {
					// all good value is good to go.
					callTree[idx] = { call, ready: true, value, awaiting: false, ttl, key, };
					idx++;
					continue;
				}

				// removing, and fetching ...
				cache.delete(key);
				fetchlog.push(key);
				callTree[idx] = { call, ready: false, value: undefined, awaiting: false, ttl, key, };
				idx++;
				continue;

			}

			// if(!fetchlog.includes(key)) {
			// 	throw new Error(`multiCaller failed to get result for call ${call.functionName.toString()} because it is not fetching --- ${JSON.stringify(cached)}`);
			// }

			callTree[idx] = { call, ready: false, value: undefined, awaiting: true, ttl, key, };
			idx++;
			continue;

		}

		fetchlog.push(key);
		cache.set(key, { since: 0, value: null, ready: false, fetching: true, });
		callTree[idx] = { call, ready: false, value: null, awaiting: false, ttl, key, };
		idx++;
	}

	const callables = Object.values(callTree).map(ITM => ITM.ready || ITM.awaiting ? null : ITM.call).filter(v => !!v);
	const responses = callables.length ? await multicall(config, { contracts: callables, }) : [];
	let lastIdx = 0;

	for(const [idx, { call, ready, value, awaiting, ttl, key, }] of Object.entries(callTree)) {

		if(ready) {
			results.push(value);
			continue;
		}


		if(awaiting) {

			if(!cache.has(key)) {
				throw new Error(`\n\nmultiCaller failed to get result for call ${call.functionName.toString()} because it is not fetching ${key} --- \n\n${JSON.stringify(Array.from(cache.keys()), null, 2)}\n\n${JSON.stringify(fetchlog, null, 2)}`);
			}
			const value = await awaitValue(key);
			results.push(value);
			continue;
		}

		const { result, error:responseErrors, } = responses[lastIdx];

		if(responseErrors) {
			if(error instanceof AggregateError) {
				error.errors.push(new Error(`${call.functionName.toString()} failed: ${responseErrors instanceof Error ? responseErrors : new Error(String(responseErrors))}`));
			}
			else {
				error = getAggregateError(responseErrors, `multiCaller failed to get result for call ${call.functionName.toString()}`);
			}
		}

		cache.set(key, { since: Date.now(), value: result, ready: true, fetching: false, });
		results.push(result);
		lastIdx++;
	}

	return [ results, error, ] as [T, AggregateError|null];
}




